/**
 * @fileOverview
 * All variables that get passed in to the mainIssueAggQuery are defined here.
 * The abstraction is necessary because there are multiple call paths to mainIssueAggQuery,
 * so having a single source of truth for the variables eliminates a vector for
 * bugs and inconsistencies when they are invoked.
 *
 * This file will be removed (and therefore the need for the linting exemption) once we reduce
 * the number of discrete call paths for mainIssueAggQuery to one. That work will be done in
 * https://jira.atlassian.com/browse/JIV-19486.
 *
 * Sorry, Tom.
 *
 * <3 Daz
 */
/* eslint-disable jira/ff/inline-usage */
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { getProductOfferingKeyOrThrow } from '@atlassian/jira-growth-utils/src/services/get-product-offering-key/index.tsx';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { FREE_EDITION, STANDARD_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import getEditions from '@atlassian/jira-common-util-get-tenant-context/src/edition/index.tsx';
import getMeta from '@atlassian/jira-get-meta';
import mainIssueAggQuery from '@atlassian/jira-relay/src/__generated__/mainIssueAggQuery$parameters';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	ISSUE_ACTION_MODULE,
	ISSUE_ACTIVITY_MODULE,
	ISSUE_CONTEXT_MODULE,
	ISSUE_GLANCE_MODULE,
	ISSUE_PANEL_MODULE,
	ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
	UI_MODIFICATIONS_MODULE,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import { getPermalinkId, getPermalinkIdFromURL, COMMENTS } from '../../common/utils/permalinks.tsx';

type ProvidedVar = (typeof mainIssueAggQuery)['params']['providedVariables'];
type ProvidedVariables = {
	[k in keyof ProvidedVar]: unknown;
};

export const getMainIssueViewAggProvidedVariables = (): ProvidedVariables => {
	const providerVariables: Record<keyof NonNullable<ProvidedVar>, { get: () => unknown }> =
		mainIssueAggQuery?.params?.providedVariables ?? {};
	return Object.entries(providerVariables).reduce(
		(acc: Record<string, unknown>, [key, provider]) => {
			acc[key] = provider.get();
			return acc;
		},
		{},
	);
};

export const getMainIssueViewAggQueryFeatureFlagVariables = () => ({
	isCompassComponentsEnabled: true,
	issueViewRelayConfigurationUrlFlag: fg('jiv-replace-configuration-button-in-context-panel'),
	jsmSentimentCustomFieldFlag: true,
	isJPDPanelEnabled: fg('jpd_idea_panel_in_issue_view'),
	issueViewRelayPriorityFieldFlag: fg('relay-migration-issue-fields-priority'),
	issueViewRelayNumberFieldFlag: fg('relay-migration-issue-fields-number'),
	issueViewRelayAttachmentsFlag: fg('relay-migration-issue-view-attachments-'),
	issueViewRelayResolutionFieldFlag: fg('relay-migration-issue-fields-resolution'),
	issueViewRelayParentFieldFlag: fg('relay-migration-issue-header-and-parent'),
	issueViewRelayDateTimeFieldFlag: fg('relay-migration-issue-fields-date-time-fg'),
	issueViewRelayMetadataFieldFlag: fg('relay-migration-issue-fields-metadata'),
	issueViewDesignsPanelFlag: expVal(
		'collapsed_designs_panel',
		'isCollapsedDesignsPanelEnabled',
		false,
	),
	issueViewDesignsPanelProvidersFlag: fg('only_show_designs_panel_with_design_providers'),
	isJPDDeliveryLinkTypeFilteringEnabled: fg('jpd_idea_panel_in_issue_view'),
	issueViewRelayUrlFieldFlag: fg('relay-migration-issue-fields-url'),
	issueViewRemoveCheckboxSelectFieldOptionsFlag: fg('remove_issue_field_options_main_query'),
	issueViewRelaySummaryFieldFlag: fg('relay-migration-issue-fields-summary'),
	issueViewRelayColorFieldFlag: fg('relay-migration-issue-fields-color'),
	issueViewRelayHeaders: fg('relay-migration-issue-header-and-parent'),
	issueViewRelayTimeTrackingFieldFlag: fg('relay-migration-issue-fields-time-tracking'),
	issueViewRemoveRadioSelectFieldOptionsFlag: fg('relay-migration-issue-fields-radio'),
	customFieldConfigurationFlag: fg('issue_view_field_config_edit'),
	isPinFieldConfigurabilityEnabled: true,
	jcsFlag: fg('jcs_master_flag'),
	issueViewSmartRepliesSettingsFlag: fg('smart-replies-system-setting') ?? false,
	isProactiveCommentSummaryPreferenceEnabled: fg('proactive_comment_summary_feature_toggle'),
	crossflowBannerDismissedInAggFlag: fg('jiv-19897-move-user-prefs-to-mainissueagg'),
	dateFieldsMessageEnabled: expValEquals('jsw_start_and_due_dates', 'cohort', 'variation'),
	chinEmbedQueryToAgg: fg('issue-jiv-19838-chin-api-lookup-through-issue-key'),
	issueViewActivityLayoutFieldEnabled: expVal(
		'issue-view-side-panel-activity',
		'isActivityInSidePanel',
		false,
	),
	issueViewDetailsPanelFieldsReorderingEnabled: expVal(
		'issue_view_field_reordering_in_context_panel',
		'isReorderingFieldsEnabled',
		false,
	),
	userThemePrefsInAggEnabled: fg('custom-nav-theme-preferences-to-mainissueagg'),
	issueViewRedactionExperience:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('enable_redaction_support_in_jira_issue_view') &&
		fg('jiv-replace-configuration-button-in-context-panel') &&
		fg('relay-migration-issue-header-and-parent'),
	issueViewCoverExperimentEnabled:
		expVal('cover-image-issue-view-experiment', 'isSharedCoverEnabled', false) ||
		expVal('cover-image-issue-view-experiment', 'isMiddleCoverEnabled', false),
	isPlaybooksEnabled: fg('playbook_in_jsm_ga'),
	isPlaybooksEnabledForSpecificType: fg('playbook_visibility_in_service_request'),
	assignToMeUserPreloaded: fg('assign-to-me-user-preloaded-in-issue-view'),
	isAutomationDiscoverabilityEnabled: fg('automation_discoverability_in_jsm'),
	issueViewCodePlannerPanelEnabled: fg('devai-code-planner-issue-view-panel'),
	issueViewChildIssuesPanelPerformance: expVal(
		'jira-issue-view-chin-m2-performance',
		'isIssueSearchAPIEnabled',
		false,
	),
	mentionedConfluencePageLinksV2PaginationFlag:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jsw_issue_view_placeholder_template_v2') &&
		fg('jiv-19125-fetch-remote-links-in-interactive-query'),
	mentionedConfluencePageLinksPaginationFlag:
		!fg('jsw_issue_view_placeholder_template_v2') &&
		fg('jiv-19125-fetch-remote-links-in-interactive-query'),
	fetchConfluenceApplinksEarlyFlag: fg('jiv-19890_fetch_confluence_applinks_early'),
	linkedWorkItemsTableEnabled: fg('jira-linked-work-items-table-kill-switch'),
});

// JIV-19906: retrieve query string from URL -- only expected to be used by client side code
// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const getURLFromWindow = () => new URL(window.location.href);

// JIV-19906: retrieve query string from url
// server-side: expect url to be passed
// client-side: read from window
export const getMainIssueViewAggQueryVariables = (
	issueKey: string | null,
	url: URL = getURLFromWindow(),
) => ({
	...getMainIssueViewAggProvidedVariables(),
	...getMainIssueViewAggQueryFeatureFlagVariables(),
	...(fg('jiv-19906-fix-targered-permalink-on-server')
		? getCommentQueriesFromURL(url)
		: getCommentQueries()),
	...getStorageBannerVariables(),
	...getIssueSearchForChildIssueQueryVariables(issueKey),
	...getForgeSpecificVariables(),
	...getIsFromSoftwareCalendarOrTimelineViewQueryVariables(),
	...getLinkedIssuesTableQueryVariables(issueKey),
});

const getIsFromSoftwareCalendarOrTimelineViewQueryVariables = () => {
	if (expValEquals('jsw_start_and_due_dates', 'cohort', 'variation')) {
		if (typeof window !== 'undefined') {
			const pathname = window.location.pathname;
			const pattern =
				/^\/jira\/software(?:\/c)?\/projects\/[^/]+\/boards\/\d+\/(calendar|timeline)$/;
			return {
				isInTimelineOrCalendarView: pattern.test(pathname),
			};
		}
	}
	return {
		isInTimelineOrCalendarView: false,
	};
};

const getChildIssuesFieldIdsQueryVariables = () => {
	/**
	 * Default fields we're fetching in child issues:
	 * - issuetype: Used to separate base-level and subtasks in epics (see `jira-issue-view-child-issues-panel-merge-subtasks`)
	 */
	const childIssuesFieldIds = ['issuetype'];

	// If we're not fetching the "Include subtasks" timetracking data through Relay
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('relay-migration-edit-worklog')) childIssuesFieldIds.push('timetracking');

	// If we're not fetching the progress summary status data through Relay,
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('issue-jiv-20335-relay-progress-summary')) childIssuesFieldIds.push('status');

	// Include unused fields
	if (!expVal('jira-issue-view-chin-m2-performance', 'isIssueSearchAPIEnabled', false)) {
		childIssuesFieldIds.push('assignee', 'created', 'priority', 'summary', 'updated');
	}

	return childIssuesFieldIds;
};

const getIssueSearchForChildIssueQueryVariables = (issueKey: string | null) => ({
	// will have to put jql order by rank later
	issueSearchInput: { childIssuesInput: { parentIssueKey: issueKey || '' } },
	amountOfColumns: 500,
	namespace: 'CHILD_ISSUE_PANEL',
	fieldSetIds: [],
	shouldQueryFieldSetsById: false,
	fieldSetsInput: {
		viewInput: {
			namespace: 'CHILD_ISSUE_PANEL',
			context: {
				issueContext: {
					issueKey,
				},
			},
		},
	},
	fieldSetsContext: { issueContext: { issueKey } },
	filterId: null,
	viewId: null,
	childIssuesFieldIds: getChildIssuesFieldIdsQueryVariables(),
});

const getLinkedIssuesTableQueryVariables = (issueKey: string | null) => ({
	linkedIssuesSearchInput: {
		jql: `issue in linkedIssues(${issueKey})`,
	},
});

export const getCommentQueries = () => {
	const commentTargetId = getPermalinkId(COMMENTS) || null;

	return {
		commentTargetId,
		hasCommentTargetId: !!commentTargetId,
	};
};

export const getCommentQueriesFromURL = (url: URL) => {
	let commentTargetId = null;

	if (fg('jiv-19906-fix-targered-permalink-on-server')) {
		commentTargetId = getPermalinkIdFromURL(COMMENTS, url);
	} else {
		commentTargetId = getPermalinkId(COMMENTS);
	}

	return {
		commentTargetId,
		hasCommentTargetId: !!commentTargetId,
	};
};

const getStorageBannerVariables = () => ({
	issueViewStorageLimitsBannerFlag:
		getEditions(getMeta).software === FREE_EDITION &&
		fg('move_storage_banner_data_fetch_to_main_issue_agg'),
	offeringKey: getProductOfferingKeyOrThrow(SOFTWARE, STANDARD_EDITION),
	productKey: SOFTWARE,
});

const getForgeSpecificVariables = () => {
	return {
		forgeInMainIssueAggFlag: fg('move_forge_data_fetch_to_main_issue_agg'),
		forgeTypes: fg('move_forge_data_fetch_to_main_issue_agg')
			? [
					ISSUE_ACTION_MODULE,
					ISSUE_GLANCE_MODULE,
					ISSUE_CONTEXT_MODULE,
					ISSUE_PANEL_MODULE,
					ISSUE_ACTIVITY_MODULE,
					CUSTOM_FIELD_MODULE,
					CUSTOM_FIELD_TYPE_MODULE,
					ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
					UI_MODIFICATIONS_MODULE,
				]
			: [],
		includeUserAccessForge: fg('forge_query_include_user_access'),
		includeUserAccessEnabledForge: fg('forge_query_include_user_access_enabled'),
	};
};
